import React, { useEffect, useState } from "react";
import { FormattedMessage, FormattedDate } from "react-intl";
import {
  Box,
  Flex,
  Stack,
  Icon,
  Text,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { IoIosAddCircleOutline } from "react-icons/io";
import { useCancelPayoutRequest } from "api/invoicing";
import ConfirmDeletePayoutModal from "./ConfirmDeletePayoutModal";
import NoPendingPayouts from "./NoPendingPayouts";
import { FormattedMoney } from "components/FormattedMoney";

const PendingPayoutsModal = ({ isOpen, onClose, onUpdate, pendingPayouts }) => {
  const [deletePayout, setDeletePayout] = useState(null);
  const toast = useToast();
  const {
    mutateAsync: cancelPayoutRequest,
    isSuccess: isCancelPayoutRequestSuccess,
    isLoading: isCancelPayoutRequestLoading,
    error: cancelPayoutRequestError,
  } = useCancelPayoutRequest();
  const {
    isOpen: isConfirmModalOpen,
    onOpen: onConfirmModalOpen,
    onClose: onConfirmModalClose,
  } = useDisclosure();

  useEffect(() => {
    if (isCancelPayoutRequestSuccess) {
      toast({
        title: "Success",
        description: "Payout request successfully cancelled",
        status: "success",
        isClosable: true,
      });
      onConfirmModalClose();
      onUpdate();
    }
  }, [isCancelPayoutRequestSuccess, onConfirmModalClose, onUpdate, toast]);

  useEffect(() => {
    const error = cancelPayoutRequestError;

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        isClosable: true,
      });
    }
  }, [cancelPayoutRequestError, toast]);

  const handleModalClose = () => {
    onConfirmModalClose();
    setDeletePayout(null);
  };
  const handleConfirmCancel = (pendingPayout) => {
    setDeletePayout(pendingPayout);
    onConfirmModalOpen();
  };
  const handleConfirmClick = () => {
    cancelPayoutRequest({ payoutId: deletePayout.transaction_id });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent
        mb="0"
        minHeight="calc(100vh - 3.75rem)"
        borderBottomRightRadius="0px"
        borderBottomLeftRadius="0px"
      >
        <ModalHeader />
        <ModalCloseButton data-testid="pending-payouts-modal-close-btn" />
        <ModalBody p="10">
          <Text fontSize="lg" fontWeight="500" textAlign="center">
            <FormattedMessage
              defaultMessage="Pending Payouts"
              description="Popup Title. Meaning, awaiting money transfers, or withdrawals."
            />
          </Text>
          <Box mt="5">
            <FormattedMessage
              defaultMessage="If you wish to cancel any pending transaction, please click Cancel Transaction button below"
              description="Informative message"
            />
          </Box>
          <Stack spacing="6" mt="7">
            {pendingPayouts.map((pendingPayout) => {
              const {
                payout_amount_less_fee,
                payout_amount_less_fee_currency,
                payout_total,
                payout_total_currency,
                payout_fee,
                payout_fee_currency,
                currency
              } = pendingPayout.transaction_data;
              return (
                <Box key={pendingPayout.transaction_id} px="6" py="4" boxShadow="base">
                  <Flex justifyContent="space-between">
                    <Box>
                      <Text>
                        <FormattedMessage
                          defaultMessage="Payout Amount"
                          description="Field label. Payout is like withdrawal."
                        />
                      </Text>
                      <Text fontSize="3xl" fontWeight="500">
                        <FormattedMoney
                          value={payout_amount_less_fee}
                          currency={payout_amount_less_fee_currency || currency}
                        />
                      </Text>
                    </Box>
                    <Box textAlign="right">
                      <Text fontSize="xs">
                        <FormattedDate
                          value={new Date(pendingPayout.transaction_date)}
                          month="long"
                          day="numeric"
                          year="numeric"
                          hour12={true}
                          hour="2-digit"
                          minute="2-digit"
                          timeZoneName="short"
                        />
                      </Text>
                      <Text fontSize="xs" color="secondaryText">
                        <FormattedMessage
                          defaultMessage="Requested:"
                          description="Label for payout requested amount"
                        />
                        &nbsp;
                        <FormattedMoney
                          value={payout_total}
                          currency={payout_total_currency || currency}
                        />
                      </Text>
                      <Text fontSize="xs" color="secondaryText">
                        <FormattedMessage
                          defaultMessage="Fee:"
                          description="Label for payout fees"
                        />
                        &nbsp;
                        <FormattedMoney
                          value={payout_fee}
                          currency={payout_fee_currency || currency}
                        />
                      </Text>
                    </Box>
                  </Flex>
                  <Flex mt="5">
                    <HStack
                      spacing="1"
                      color="integrator.400"
                      fontWeight="600"
                      cursor="pointer"
                      onClick={() => handleConfirmCancel(pendingPayout)}
                    >
                      <Icon as={IoIosAddCircleOutline} w="5" h="5" transform="rotate(45deg)" />
                      <Text>
                        <FormattedMessage
                          defaultMessage="Cancel Transaction"
                          description="Button label"
                        />
                      </Text>
                    </HStack>
                  </Flex>
                </Box>
              );
            })}
            {!pendingPayouts.length && <NoPendingPayouts />}
          </Stack>
        </ModalBody>
      </ModalContent>
      <ConfirmDeletePayoutModal
        isOpen={isConfirmModalOpen}
        onClose={handleModalClose}
        onConfirm={handleConfirmClick}
        isLoading={isCancelPayoutRequestLoading}
      />
    </Modal>
  );
};

export default PendingPayoutsModal;
